import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import type { DropdownOption } from '@ui';
import { OnboardingApiService } from '@shared/services/onboarding-api.service';
import type { AddressMetadata, AddressBlock, CodeTextRef } from '@shared/interfaces/opportunity.interface';
import type { HolderContactData, HolderAddressData } from '@shared/interfaces/onboarding-state.interface';
import type { PlaceDetails } from '@shared/services/google-places.service';
import { HOLDER_API, ADDRESS_SUPPORT_TYPE_KEY } from '@constants';
import { isNullOrUndefined, valueOrEmpty } from '@utils/predicates';
import { translateOption } from '@utils/translate-option';

export interface AddressFormValues {
  addressSearch: string | null;
  cityText: string | null;
  premise: string | null;
  streetName: string | null;
  houseNumber: string | null;
  floor: string | null;
  postalCode: string | null;
  province: string | null;
  country: string | null;
  docType: string | null;
  mailingCityText: string | null;
  mailingPremise: string | null;
  mailingStreetName: string | null;
  mailingHouseNumber: string | null;
  mailingFloor: string | null;
  mailingPostalCode: string | null;
  mailingProvince: string | null;
}

export interface SubmitParams {
  formValues: AddressFormValues;
  contactData: HolderContactData;
  holderNumber: typeof HOLDER_API[keyof typeof HOLDER_API];
  selectedCountryCode: string;
  selectedCountryName: string;
  mailingSameAsResidence: boolean;
  uploadedFile: File | null;
  docTypeOptions: DropdownOption[];
  provinceOptions: DropdownOption[];
  placeDetails: PlaceDetails | null;
}

function getOptionCode(options: DropdownOption[], selectedId: string | null | undefined): string {
  if (!selectedId) {
    return '';
  }
  const option = options.find((o) => o.id === selectedId);
  return valueOrEmpty(option?.code);
}

function getOptionName(options: DropdownOption[], selectedId: string | null | undefined): string {
  if (!selectedId) {
    return '';
  }
  const option = options.find((o) => o.id === selectedId);
  return valueOrEmpty(option?.name);
}

interface LoadResult {
  options: DropdownOption[];
  preselectId: string | null;
}

const EMPTY_LOAD_RESULT: LoadResult = { options: [], preselectId: null };

@Injectable({ providedIn: 'root' })
export class AddressSubmitService {
  private readonly onboardingApi = inject(OnboardingApiService);
  private readonly translate = inject(TranslateService);

  submit(params: SubmitParams): Observable<HolderAddressData> {
    const {
      formValues, contactData, holderNumber, selectedCountryCode, selectedCountryName,
      mailingSameAsResidence, uploadedFile, docTypeOptions, provinceOptions,
      placeDetails
    } = params;

    const residenceBlock = this.buildAddressBlock(formValues, selectedCountryCode, selectedCountryName, provinceOptions);
    const mailingBlock = mailingSameAsResidence
      ? { ...residenceBlock }
      : this.buildMailingAddressBlock(formValues, selectedCountryCode, selectedCountryName, provinceOptions);
    const docTypeCode = getOptionCode(docTypeOptions, formValues.docType);
    const docTypeName = getOptionName(docTypeOptions, formValues.docType);

    const metadata: AddressMetadata = {
      cuid: contactData.cuid,
      oppId: contactData.opportunityId,
      holder: holderNumber,
      mailingSameAsResidence,
      residenceAddress: residenceBlock,
      mailingAddress: mailingBlock,
      placeId: valueOrEmpty(placeDetails?.placeId),
      latitude: placeDetails?.latitude ?? null,
      longitude: placeDetails?.longitude ?? null,
      premise: valueOrEmpty(formValues.premise),
      sublocality: valueOrEmpty(placeDetails?.sublocality),
      administrativeAreaLevel1: valueOrEmpty(placeDetails?.administrativeAreaLevel1),
      administrativeAreaLevel3: valueOrEmpty(placeDetails?.administrativeAreaLevel3),
      postalTown: valueOrEmpty(placeDetails?.postalTown),
      colloquialArea: valueOrEmpty(placeDetails?.colloquialArea),
      addressSupportType: { code: docTypeCode, text: docTypeName }
    };

    return this.onboardingApi.sendAddress(metadata, uploadedFile).pipe(
      map(() => this.buildHolderData(
        formValues, selectedCountryCode, selectedCountryName, mailingSameAsResidence,
        docTypeCode, docTypeName, provinceOptions, placeDetails
      ))
    );
  }

  private buildAddressBlock(
    fv: AddressFormValues,
    countryCode: string,
    countryName: string,
    provinceOptions: DropdownOption[]
  ): AddressBlock {
    const provinceCode = getOptionCode(provinceOptions, fv.province);
    const provinceName = getOptionName(provinceOptions, fv.province);
    const provinceRef: CodeTextRef = { code: provinceCode, text: provinceName };

    return {
      streetPrimaryAddress: this.buildFullAddress(fv),
      route: [fv.premise, fv.streetName].filter(Boolean).join(' '),
      streetNumberPrimaryAddress: valueOrEmpty(fv.houseNumber).replace(/^#/, ''),
      apartmentPrimaryAddress: valueOrEmpty(fv.floor),
      cityPrimaryAddress: { code: provinceCode || 'N/A', text: valueOrEmpty(fv.cityText) },
      statePrimaryAddress: provinceRef,
      zipCodePrimaryAddress: valueOrEmpty(fv.postalCode),
      country: { code: countryCode, text: countryName }
    };
  }

  private buildMailingAddressBlock(
    fv: AddressFormValues,
    countryCode: string,
    countryName: string,
    provinceOptions: DropdownOption[]
  ): AddressBlock {
    const provinceCode = getOptionCode(provinceOptions, fv.mailingProvince);
    const provinceName = getOptionName(provinceOptions, fv.mailingProvince);
    const provinceRef: CodeTextRef = { code: provinceCode, text: provinceName };

    const mailingFv: AddressFormValues = {
      ...fv,
      premise: fv.mailingPremise,
      streetName: fv.mailingStreetName,
      houseNumber: fv.mailingHouseNumber,
      floor: fv.mailingFloor,
      cityText: fv.mailingCityText,
      postalCode: fv.mailingPostalCode,
      province: fv.mailingProvince
    };

    return {
      streetPrimaryAddress: this.buildFullAddress(mailingFv),
      route: [fv.mailingPremise, fv.mailingStreetName].filter(Boolean).join(' '),
      streetNumberPrimaryAddress: valueOrEmpty(fv.mailingHouseNumber).replace(/^#/, ''),
      apartmentPrimaryAddress: valueOrEmpty(fv.mailingFloor),
      cityPrimaryAddress: { code: provinceCode || 'N/A', text: valueOrEmpty(fv.mailingCityText) },
      statePrimaryAddress: provinceRef,
      zipCodePrimaryAddress: valueOrEmpty(fv.mailingPostalCode),
      country: { code: countryCode, text: countryName }
    };
  }

  private buildHolderData(
    fv: AddressFormValues,
    countryCode: string,
    countryName: string,
    mailingSameAsResidence: boolean,
    docTypeCode: string,
    docTypeName: string,
    provinceOptions: DropdownOption[],
    placeDetails: PlaceDetails | null
  ): HolderAddressData {
    return {
      streetAddress: this.buildFullAddress(fv),
      houseNumber: valueOrEmpty(fv.houseNumber),
      apartment: valueOrEmpty(fv.floor),
      cityCode: '',
      cityName: valueOrEmpty(fv.cityText),
      provinceCode: getOptionCode(provinceOptions, fv.province),
      provinceName: getOptionName(provinceOptions, fv.province),
      postalCode: valueOrEmpty(fv.postalCode),
      countryCode,
      countryName,
      premise: valueOrEmpty(fv.premise),
      streetName: valueOrEmpty(fv.streetName),
      placeId: valueOrEmpty(placeDetails?.placeId),
      latitude: placeDetails?.latitude ?? null,
      longitude: placeDetails?.longitude ?? null,
      mailingSameAsResidence,
      documentTypeCode: docTypeCode,
      documentTypeName: docTypeName,
      mailingCityName: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingCityText),
      mailingPremise: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingPremise),
      mailingStreetName: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingStreetName),
      mailingHouseNumber: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingHouseNumber),
      mailingFloor: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingFloor),
      mailingPostalCode: mailingSameAsResidence ? '' : valueOrEmpty(fv.mailingPostalCode),
      mailingProvinceCode: mailingSameAsResidence ? '' : getOptionCode(provinceOptions, fv.mailingProvince),
      mailingProvinceName: mailingSameAsResidence ? '' : getOptionName(provinceOptions, fv.mailingProvince)
    };
  }

  private buildFullAddress(fv: AddressFormValues): string {
    const parts = [
      fv.premise, fv.streetName, fv.houseNumber,
      fv.cityText, fv.province, fv.postalCode
    ].filter(Boolean);
    return parts.join(', ');
  }

  loadDocTypes(preselect?: string): Observable<LoadResult> {
    return this.onboardingApi.getDocTypes().pipe(
      map((response) => {
        if (isNullOrUndefined(response)) {
          return EMPTY_LOAD_RESULT;
        }
        const raw = response[ADDRESS_SUPPORT_TYPE_KEY];
        if (!raw) {
          return EMPTY_LOAD_RESULT;
        }
        const options: DropdownOption[] = raw.map((d) => ({
          id: d.code,
          name: this.translate.instant(valueOrEmpty(d.value?.key)),
          code: d.code
        }));
        let preselectId: string | null = null;
        if (preselect) {
          const matched = options.find((o) => o.name === preselect);
          if (matched) {
            preselectId = matched.id;
          }
        }
        return { options, preselectId };
      }),
      catchError(() => of(EMPTY_LOAD_RESULT))
    );
  }

  loadProvinces(
    countryCode: string,
    preselect?: string
  ): Observable<LoadResult> {
    if (countryCode == '') return of(EMPTY_LOAD_RESULT)
    return this.onboardingApi.getStates(countryCode).pipe(
      map((states) => {
        const options: DropdownOption[] = states.map((s) => ({
          id: translateOption(s.name, this.translate),
          name: translateOption(s.name, this.translate),
          code: s.code
        }));
        let preselectId: string | null = null;
        if (preselect) {
          const matched = options.find((o) => o.name?.toLowerCase() === preselect.toLowerCase());
          if (matched) {
            preselectId = matched.id;
          }
        }
        return { options, preselectId };
      }),
      catchError(() => of(EMPTY_LOAD_RESULT))
    );
  }
}
